import { useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { usePortalPosition } from '@/hooks/usePortalPosition';

// Reuses the SearchInput look (same classes) so the unified location field and
// the Keyword field read as siblings inside the hero search panel.
import '../SearchInput/SearchInput.css';
import './LocationSearchInput.css';

export type LocationSuggestionType = 'building' | 'community' | 'zone';

export type LocationSuggestion = {
  /** Location text exactly as stored on the deal (zone / community / building).
   *  Search is text-first (addendum ADR-045 §8) — no canonical key here. */
  value: string;
  type: LocationSuggestionType;
  dealCount: number;
};

const MIN_FREE_TEXT = 2;

export type LocationSearchInputProps = {
  /** Committed display value — the active location text, or '' when no location
   * filter is active. Typing stages a query; the committed value changes
   * through `onSelect` (pick) or `onSubmitText` (free text). */
  value: string;
  /** Synchronous client-side lookup over the (tiny) location corpus. An empty
   * query should return the top suggestions (discoverability on focus). */
  suggestionsFor: (query: string) => LocationSuggestion[];
  /** A suggestion was picked. */
  onSelect: (suggestion: LocationSuggestion) => void;
  /** Free text was submitted (Enter or the "search «q»" row) — lets a deal
   * whose location is not a corpus entry still be found by text. */
  onSubmitText: (text: string) => void;
  onClear: () => void;
  /** Localised badge text per suggestion type (Building / Community / Zone). */
  badgeLabels: Record<LocationSuggestionType, string>;
  /** Localised label for the free-text row, e.g. q => `Search “${q}”`. */
  freeTextLabel: (query: string) => string;
  placeholder?: string;
  ariaLabel: string;
  clearAriaLabel: string;
};

type Item =
  | { kind: 'suggestion'; suggestion: LocationSuggestion }
  | { kind: 'freetext'; query: string };

export function LocationSearchInput({
  value,
  suggestionsFor,
  onSelect,
  onSubmitText,
  onClear,
  badgeLabels,
  freeTextLabel,
  placeholder,
  ariaLabel,
  clearAriaLabel,
}: LocationSearchInputProps) {
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  // The input mirrors the committed value; typing diverges it into a query.
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const trimmed = query.trim();
  const suggestions = open ? suggestionsFor(query) : [];
  const hasExact = suggestions.some((s) => s.value.toLowerCase() === trimmed.toLowerCase());
  const showFreeText = open && trimmed.length >= MIN_FREE_TEXT && !hasExact;
  const items: Item[] = [
    ...suggestions.map((suggestion) => ({ kind: 'suggestion' as const, suggestion })),
    ...(showFreeText ? [{ kind: 'freetext' as const, query: trimmed }] : []),
  ];

  const { panelStyle } = usePortalPosition({
    triggerRef: inputRef,
    open: open && items.length > 0,
    matchTriggerWidth: true,
    onTriggerOutOfView: () => {
      setOpen(false);
      setActiveIdx(-1);
    },
  });

  // Click outside: close and revert the staged query to the committed value.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (rootRef.current.contains(target)) return;
      if (listboxRef.current && listboxRef.current.contains(target)) return;
      setOpen(false);
      setActiveIdx(-1);
      setQuery(value);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, value]);

  const commitSuggestion = (s: LocationSuggestion) => {
    onSelect(s);
    setQuery(s.value);
    setOpen(false);
    setActiveIdx(-1);
    inputRef.current?.blur();
  };

  const commitFreeText = (text: string) => {
    onSubmitText(text);
    setQuery(text);
    setOpen(false);
    setActiveIdx(-1);
    inputRef.current?.blur();
  };

  const commitItem = (item: Item) => {
    if (item.kind === 'suggestion') commitSuggestion(item.suggestion);
    else commitFreeText(item.query);
  };

  const clear = () => {
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
    onClear();
    inputRef.current?.focus();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setActiveIdx(-1);
    if (!open) setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (open) {
        setOpen(false);
        setQuery(value);
      } else if (value) {
        clear();
      }
      return;
    }
    if (event.key === 'Enter') {
      if (open && activeIdx >= 0 && items[activeIdx]) {
        event.preventDefault();
        commitItem(items[activeIdx]);
      } else if (trimmed.length >= MIN_FREE_TEXT) {
        // Bare Enter on a typed term: search it as free text (the location may
        // not be a corpus entry — NULL-key deals must still be reachable).
        event.preventDefault();
        commitFreeText(trimmed);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (items.length > 0) setActiveIdx((i) => (i + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (open && items.length > 0) {
        setActiveIdx((i) => (i <= 0 ? items.length - 1 : i - 1));
      }
    } else if (event.key === 'Tab') {
      setOpen(false);
      setQuery(value);
    }
  };

  return (
    <div ref={rootRef} className="search-input location-search">
      <div className="search-input__field">
        <span className="search-input__icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 1.5a4.5 4.5 0 0 1 4.5 4.5c0 3.2-4.5 8.5-4.5 8.5S3.5 9.2 3.5 6A4.5 4.5 0 0 1 8 1.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <circle cx="8" cy="6" r="1.75" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          value={query}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open && items.length > 0}
          aria-activedescendant={open && activeIdx >= 0 ? `${baseId}-opt-${activeIdx}` : undefined}
          autoComplete="off"
          spellCheck={false}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="search-input__input"
        />
        {value || query ? (
          <button
            type="button"
            className="search-input__clear"
            onClick={clear}
            aria-label={clearAriaLabel}
          >
            ×
          </button>
        ) : null}
      </div>
      {open && items.length > 0 && typeof document !== 'undefined'
        ? createPortal(
            <ul
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              className="search-input__listbox"
              style={panelStyle}
            >
              {items.map((item, i) =>
                item.kind === 'suggestion' ? (
                  <li
                    key={`${item.suggestion.type}-${item.suggestion.value}-${i}`}
                    id={`${baseId}-opt-${i}`}
                    role="option"
                    aria-selected={i === activeIdx}
                    className={`search-input__option location-search__option${
                      i === activeIdx ? ' search-input__option--active' : ''
                    }`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      commitSuggestion(item.suggestion);
                    }}
                  >
                    <span className="location-search__option-label">
                      {item.suggestion.value}
                      <span className="location-search__option-count" aria-hidden="true">
                        {' '}
                        · {item.suggestion.dealCount}
                      </span>
                    </span>
                    <span
                      className={`location-search__badge location-search__badge--${item.suggestion.type}`}
                    >
                      {badgeLabels[item.suggestion.type]}
                    </span>
                  </li>
                ) : (
                  <li
                    key={`freetext-${i}`}
                    id={`${baseId}-opt-${i}`}
                    role="option"
                    aria-selected={i === activeIdx}
                    className={`search-input__option location-search__option location-search__option--freetext${
                      i === activeIdx ? ' search-input__option--active' : ''
                    }`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      commitFreeText(item.query);
                    }}
                  >
                    <span className="location-search__option-label">
                      {freeTextLabel(item.query)}
                    </span>
                  </li>
                ),
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
